import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import deviceGroupService from "../services/deviceGroupService";
import locationService from "../services/locationService";
import macScriptService from "../services/macScriptService";

const DEFAULT_SCRIPT = `#!/bin/bash
set -euo pipefail
# Beispiel: Ordner für den Nutzer anlegen
# mkdir -p "$HOME/Documents/Schule"
`;

const emptyForm = () => ({
  name: "",
  description: "",
  enabled: true,
  enableMcxLoginScripts: true,
  location: "",
  groups: [],
  scripts: [
    {
      name: "Login",
      filename: "login.sh",
      trigger: "login",
      content: DEFAULT_SCRIPT,
    },
  ],
});

function formFromBundle(bundle) {
  return {
    name: bundle.name || "",
    description: bundle.description || "",
    enabled: bundle.enabled !== false,
    enableMcxLoginScripts: bundle.enableMcxLoginScripts !== false,
    location: bundle.location?._id || bundle.location || "",
    groups: (bundle.groups || []).map((g) => g._id || g).filter(Boolean),
    scripts: (bundle.scripts || []).length
      ? bundle.scripts.map((row) => ({
          name: row.name || "",
          filename: row.filename || "script.sh",
          trigger: row.trigger === "logout" ? "logout" : "login",
          content: row.content || "#!/bin/bash\n",
        }))
      : emptyForm().scripts,
  };
}

export default function MacScripts() {
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();
  const [bundles, setBundles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [bundleList, groupList, locationList] = await Promise.all([
        macScriptService.listBundles(),
        deviceGroupService.getDeviceGroups().catch(() => []),
        locationService.getLocations().catch(() => []),
      ]);
      setBundles(bundleList);
      setGroups(Array.isArray(groupList) ? groupList : []);
      setLocations(Array.isArray(locationList) ? locationList : []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateScript = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      scripts: prev.scripts.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const addScript = (trigger = "login") => {
    setForm((prev) => ({
      ...prev,
      scripts: [
        ...prev.scripts,
        {
          name: trigger === "logout" ? "Logout" : "Login",
          filename: `${trigger}.sh`,
          trigger,
          content: DEFAULT_SCRIPT,
        },
      ],
    }));
  };

  const removeScript = (index) => {
    setForm((prev) => ({
      ...prev,
      scripts: prev.scripts.filter((_, i) => i !== index),
    }));
  };

  const startCreate = () => {
    setEditingId("new");
    setForm(emptyForm());
  };

  const startEdit = (bundle) => {
    setEditingId(bundle._id);
    setForm(formFromBundle(bundle));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const payloadFromForm = () => ({
    name: form.name,
    description: form.description,
    enabled: form.enabled,
    enableMcxLoginScripts: form.enableMcxLoginScripts,
    location: form.location || null,
    groups: form.groups,
    scripts: form.scripts,
    publish: true,
  });

  const save = async () => {
    if (!admin) return;
    if (!form.name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    if (!form.scripts.length) {
      toast.error("Mindestens ein Skript erforderlich");
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        await macScriptService.createBundle(payloadFromForm());
        toast.success("Skript-Paket angelegt und Profil veröffentlicht");
      } else {
        await macScriptService.updateBundle(editingId, payloadFromForm());
        toast.success("Skript-Paket gespeichert und Profil aktualisiert");
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!admin) return;
    if (!window.confirm("Skript-Paket und zugehöriges Profil löschen?")) return;
    try {
      await macScriptService.deleteBundle(id);
      toast.success("Gelöscht");
      if (editingId === id) cancelEdit();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Löschen fehlgeschlagen");
    }
  };

  const groupOptions = useMemo(
    () => groups.map((g) => ({ id: g._id, name: g.name || g._id })),
    [groups],
  );

  if (!user) return null;

  return (
    <div className="py-3">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
        <div>
          <h1 className="h3 mb-1">Mac-Skripte</h1>
          <p className="text-muted mb-0">
            Login-/Logout-Skripte als Profil-Pakete. Unter der Haube:{' '}
            <code>com.apple.mcxloginscripts</code>. Einzelne Payloads auch im{' '}
            <Link to="/profiles/editor">Profil-Editor</Link> (Zielgerät Mac).
          </p>
        </div>
        {admin ? (
          <button type="button" className="btn btn-primary" onClick={startCreate} disabled={!!editingId}>
            Neues Paket
          </button>
        ) : null}
      </div>

      {editingId ? (
        <div className="card mb-4">
          <div className="card-body">
            <h2 className="h5 mb-3">{editingId === "new" ? "Neues Skript-Paket" : "Skript-Paket bearbeiten"}</h2>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  disabled={saving}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Standort (optional)</label>
                <select
                  className="form-select"
                  value={form.location}
                  disabled={saving}
                  onChange={(e) => setField("location", e.target.value)}
                >
                  <option value="">—</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Beschreibung</label>
                <input
                  className="form-control"
                  value={form.description}
                  disabled={saving}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Geräte-Gruppen</label>
                <select
                  className="form-select"
                  multiple
                  size={Math.min(6, Math.max(3, groupOptions.length || 3))}
                  value={form.groups}
                  disabled={saving}
                  onChange={(e) => {
                    const selected = [...e.target.selectedOptions].map((o) => o.value);
                    setField("groups", selected);
                  }}
                >
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <div className="form-text">Das erzeugte Profil wird diesen Gruppen zugewiesen.</div>
              </div>
              <div className="col-12 d-flex flex-wrap gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="bundleEnabled"
                    checked={form.enabled}
                    disabled={saving}
                    onChange={(e) => setField("enabled", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="bundleEnabled">Aktiv</label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="enableMcx"
                    checked={form.enableMcxLoginScripts}
                    disabled={saving}
                    onChange={(e) => setField("enableMcxLoginScripts", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="enableMcx">
                    EnableMCXLoginScripts mitschreiben
                  </label>
                </div>
              </div>
            </div>

            <hr className="my-4" />
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h3 className="h6 mb-0">Skripte</h3>
              <div className="btn-group">
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={saving} onClick={() => addScript("login")}>
                  + Login
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={saving} onClick={() => addScript("logout")}>
                  + Logout
                </button>
              </div>
            </div>

            {form.scripts.map((row, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <div className="row g-2 align-items-end mb-2">
                  <div className="col-md-3">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control form-control-sm"
                      value={row.name}
                      disabled={saving}
                      onChange={(e) => updateScript(index, { name: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Dateiname</label>
                    <input
                      className="form-control form-control-sm"
                      value={row.filename}
                      disabled={saving}
                      onChange={(e) => updateScript(index, { filename: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Trigger</label>
                    <select
                      className="form-select form-select-sm"
                      value={row.trigger}
                      disabled={saving}
                      onChange={(e) => updateScript(index, { trigger: e.target.value })}
                    >
                      <option value="login">Login</option>
                      <option value="logout">Logout</option>
                    </select>
                  </div>
                  <div className="col-md-3 text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={saving || form.scripts.length <= 1}
                      onClick={() => removeScript(index)}
                    >
                      Entfernen
                    </button>
                  </div>
                </div>
                <textarea
                  className="form-control font-monospace"
                  style={{ minHeight: 160, fontSize: 13 }}
                  spellCheck={false}
                  disabled={saving}
                  value={row.content}
                  onChange={(e) => updateScript(index, { content: e.target.value })}
                />
              </div>
            ))}

            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
                {saving ? "Speichern…" : "Speichern & veröffentlichen"}
              </button>
              <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={cancelEdit}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="text-muted">Laden…</p>
      ) : !bundles.length ? (
        <p className="text-muted">Noch keine Skript-Pakete.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Skripte</th>
                <th>Gruppen</th>
                <th>Profil</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bundles.map((bundle) => (
                <tr key={bundle._id}>
                  <td>
                    <div className="fw-semibold">{bundle.name}</div>
                    {bundle.description ? <div className="small text-muted">{bundle.description}</div> : null}
                  </td>
                  <td className="small">
                    {(bundle.scripts || []).map((s) => (
                      <div key={s._id || s.filename}>{s.trigger}: {s.filename}</div>
                    ))}
                  </td>
                  <td className="small">
                    {(bundle.groups || []).map((g) => g.name || g).join(", ") || "—"}
                  </td>
                  <td className="small">
                    {bundle.profile ? (
                      <Link to={`/profiles/editor/${bundle.profile._id || bundle.profile}`}>
                        {bundle.profile.PayloadDisplayName || "Profil"}
                      </Link>
                    ) : "—"}
                  </td>
                  <td>
                    <span className={`badge ${bundle.enabled ? "text-bg-success" : "text-bg-secondary"}`}>
                      {bundle.enabled ? "aktiv" : "aus"}
                    </span>
                  </td>
                  <td className="text-end text-nowrap">
                    {admin ? (
                      <>
                        <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(bundle)}>
                          Bearbeiten
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(bundle._id)}>
                          Löschen
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
